import { useState, useEffect } from 'react';
import axios from 'axios';
import { Loader2, Save, Lock } from 'lucide-react';

const API = '/api';
const INK = '#1E1E1E';
const DIM = '#5B6470';

/**
 * Settings → Ad audiences (Randall, 18 Sep 2026). The WHO of a Meta ad, edited once here, not
 * per ad. An ad keeps a snapshot of the preset it was given — editing a preset never silently
 * changes an ad Randall approved; re-applying it on the ad does, and voids that approval.
 *
 * Its own request and its own error: a failure costs this card, never the rest of Settings.
 * Light theme: opaque hex colours only.
 */
const list = (s) => String(s || '').split(',').map((x) => x.trim()).filter(Boolean);

function PresetCard({ p, onSaved }) {
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const start = () => setEdit({
    interests: (p.interests || []).join(', '), job_titles: (p.job_titles || []).join(', '),
    age_min: p.age_min, age_max: p.age_max, radius_miles: p.radius_miles,
  });
  const save = async () => {
    setBusy(true); setErr('');
    try {
      const body = { interests: list(edit.interests), job_titles: list(edit.job_titles),
                     age_min: Number(edit.age_min), age_max: Number(edit.age_max) };
      if (p.near_fitters) body.radius_miles = Number(edit.radius_miles);
      const r = await axios.put(`${API}/social/drafts/ad-audiences/${p.id}`, body);
      onSaved(r.data); setEdit(null);
    } catch (e) { setErr(e?.response?.data?.detail || 'Could not save.'); }
    setBusy(false);
  };
  return (
    <div data-testid={`audience-${p.id}`} className="bg-hg-surface border border-hg-border rounded-sm p-4 space-y-2 text-xs font-sans"
         style={{ color: DIM, opacity: p.disabled ? 0.75 : 1 }}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm" style={{ color: INK }}>{p.name}</div>
        {p.disabled && <span className="flex items-center gap-1" style={{ color: '#7A4100' }}><Lock size={12} /> disabled</span>}
      </div>
      {p.disabled && <p style={{ color: '#7A4100' }}>{p.disabled_reason}</p>}
      {!edit && !p.disabled && (
        <>
          <div><strong style={{ color: INK }}>Interests:</strong> {(p.interests || []).join(', ') || '—'}</div>
          <div><strong style={{ color: INK }}>Job titles:</strong> {(p.job_titles || []).join(', ') || '—'}</div>
          <div><strong style={{ color: INK }}>Ages:</strong> {p.age_min}–{p.age_max} ·{' '}
            <strong style={{ color: INK }}>Where:</strong>{' '}
            {p.near_fitters ? `within ${p.radius_miles} mi of every active fitter` : (p.countries || []).join(', ')}
          </div>
          <p>Names are matched to Meta's own interest and job-title names when an ad is pushed; a name Meta does not know stops the push and is named.</p>
          <button onClick={start} className="border rounded-sm px-3 py-1" style={{ color: INK, borderColor: '#C9CED6' }}>Edit</button>
        </>
      )}
      {edit && (
        <div className="space-y-2">
          <label className="block">Interests (comma-separated)
            <input value={edit.interests} onChange={(e) => setEdit({ ...edit, interests: e.target.value })}
                   className="w-full border rounded-sm px-2 py-1" style={{ color: INK }} />
          </label>
          <label className="block">Job titles (comma-separated)
            <input value={edit.job_titles} onChange={(e) => setEdit({ ...edit, job_titles: e.target.value })}
                   className="w-full border rounded-sm px-2 py-1" style={{ color: INK }} />
          </label>
          <div className="flex gap-2">
            <label>Youngest <input type="number" min="18" max="65" value={edit.age_min}
                   onChange={(e) => setEdit({ ...edit, age_min: e.target.value })} className="w-20 border rounded-sm px-2 py-1" style={{ color: INK }} /></label>
            <label>Oldest <input type="number" min="18" max="65" value={edit.age_max}
                   onChange={(e) => setEdit({ ...edit, age_max: e.target.value })} className="w-20 border rounded-sm px-2 py-1" style={{ color: INK }} /></label>
            {p.near_fitters && (
              <label>Radius (mi) <input type="number" min="15" max="50" value={edit.radius_miles}
                     onChange={(e) => setEdit({ ...edit, radius_miles: e.target.value })} className="w-20 border rounded-sm px-2 py-1" style={{ color: INK }} /></label>
            )}
          </div>
          {err && <div style={{ color: '#8B1A1A' }}>{err}</div>}
          <div className="flex gap-2">
            <button disabled={busy} onClick={save} className="flex items-center gap-1 rounded-sm px-3 py-1 text-white disabled:opacity-50" style={{ background: '#1F6F2A' }}>
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
            </button>
            <button onClick={() => setEdit(null)} className="px-3 py-1" style={{ color: DIM }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdAudiences() {
  const [presets, setPresets] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    axios.get(`${API}/social/drafts/ad-audiences`)
      .then((r) => setPresets(r.data.presets || []))
      .catch(() => setError('Ad audiences could not be read right now.'));
  }, []);
  const saved = (p) => setPresets((ps) => ps.map((x) => (x.id === p.id ? p : x)));
  return (
    <div className="space-y-3" data-testid="ad-audiences">
      <h2 className="text-xs font-sans text-hg-text-dim uppercase tracking-wider">Ad audiences (Meta)</h2>
      {error && <p className="text-xs font-sans" style={{ color: '#8B1A1A' }}>{error}</p>}
      {presets && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {presets.map((p) => <PresetCard key={p.id} p={p} onSaved={saved} />)}
        </div>
      )}
    </div>
  );
}
